import {
	IconBook2,
	IconCalendarEvent,
	IconDownload,
	IconRocket,
	IconTrendingUp,
	IconUsersGroup,
} from "@tabler/icons-react"
import { motion, useReducedMotion } from "motion/react"
import { type ComponentType, type FC, useEffect, useState } from "react"
import { Navigate, useSearchParams } from "react-router"

import { type PlatformIntent } from "@forento/core/models/platform"
import dayjs from "@forento/core/utilities/dayjs"

import PartialLoadingPage from "~/components/PartialLoadingPage"
import { usePlatform, useUser } from "~/contexts/UserContext"
import routes from "~/utilities/routes"
import trpc from "~/utilities/trpc"

import Layout, { Logo } from "../Layout"
import ChoiceCard from "../Layout/ChoiceCard"

type Answer =
	| { id: "hasExistingBusiness"; value: boolean; time: number }
	| { id: "intent"; value: PlatformIntent; time: number }

type Option = {
	title: string
	description: string
	icon: ComponentType<{ className?: string }>
	accent: string
	onClick(): void
}

const SelectIntentPage: FC = () => {
	const user = useUser()
	const platform = usePlatform()
	const [searchParams] = useSearchParams()
	const prefersReducedMotion = useReducedMotion()

	const [startDate, setStartDate] = useState(dayjs())
	const [answers, setAnswers] = useState<Answer[]>([])

	function getAndResetTime(): number {
		const time = Math.floor(dayjs().diff(startDate, "milliseconds") / 10) / 100
		setStartDate(dayjs())
		return time
	}

	function selectIntent(value: PlatformIntent) {
		setAnswers(c => [...c, { id: "intent", value, time: getAndResetTime() }])
	}

	function selectExistingBusiness(value: boolean) {
		setAnswers(c => [...c, { id: "hasExistingBusiness", value, time: getAndResetTime() }])
	}

	const questions: { title: string; description: string; options: Option[] }[] = [
		{
			title: "What are you planning to build?",
			description: "We'll point you to the right tools first.",
			options: [
				{
					title: "Sell a course",
					description: "Step-by-step lessons and quizzes",
					icon: IconBook2,
					accent: "bg-[#fff0bc] text-[#8a5d00]",
					onClick: () => selectIntent("course"),
				},
				{
					title: "Sell an e-book",
					description: "Files, guides and templates",
					icon: IconDownload,
					accent: "bg-[#dff7e8] text-[#167246]",
					onClick: () => selectIntent("ebook"),
				},
				{
					title: "Create a coaching program",
					description: "Sell your time and expertise",
					icon: IconCalendarEvent,
					accent: "bg-[#ffe3dc] text-[#a5412b]",
					onClick: () => selectIntent("coaching"),
				},
				{
					title: "Create a community",
					description: "A home for your audience",
					icon: IconUsersGroup,
					accent: "bg-[#f4e7ff] text-[#7540a4]",
					onClick: () => selectIntent("community"),
				},
			],
		},
		{
			title: "Where are you starting from?",
			description: "Either way, you're in the right place.",
			options: [
				{
					title: "I'm building a new business",
					description: "Starting something brand new",
					icon: IconRocket,
					accent: "bg-[#dff4ff] text-[#176b91]",
					onClick: () => selectExistingBusiness(false),
				},
				{
					title: "I'm expanding an existing business",
					description: "Adding a new revenue stream",
					icon: IconTrendingUp,
					accent: "bg-[#dff7e8] text-[#167246]",
					onClick: () => selectExistingBusiness(true),
				},
			],
		},
	]

	const question = questions[answers.length]

	useEffect(() => {
		if (question !== undefined) return

		async function submitData() {
			await trpc.platform.setIntent.mutate(
				answers.reduce(
					(value, answer) => ({ ...value, [answer.id]: answer.value, [`${answer.id}Time`]: answer.time }),
					{
						intent: undefined!,
						hasExistingBusiness: undefined!,
						intentTime: undefined!,
						hasExistingBusinessTime: undefined!,
					} as Parameters<typeof trpc.platform.setIntent.mutate>[0],
				),
			)
			await user.reload()
		}

		submitData()
	}, [answers, question, user])

	if (platform!.hasChosenIntent) {
		return <Navigate to={searchParams.get("next") ?? routes.account.selectPlan()} />
	}

	return (
		<Layout>
			{question !== undefined ? (
				<main className="flex w-full max-w-2xl flex-col items-center py-8 sm:py-12">
					<Logo className="mb-8" />
					<motion.div
						key={`header-${answers.length}`}
						initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.4 }}
						className="flex max-w-xl flex-col items-center text-center"
					>
						<p className="mb-3 rounded-full bg-[#ecfccb] px-3 py-1 text-xs font-bold tracking-[0.16em] text-[#4d7c0f] uppercase">
							Question {answers.length + 1} of {questions.length}
						</p>
						<h1 className="text-3xl leading-tight font-bold tracking-tight text-slate-950 sm:text-4xl">
							{question.title}
						</h1>
						<p className="mt-3 text-base leading-relaxed font-medium text-slate-500">
							{question.description}
						</p>
					</motion.div>

					<div className="mt-9 grid w-full gap-3 sm:grid-cols-2">
						{question.options.map((option, index) => (
							<motion.div
								key={`${answers.length}-${option.title}`}
								initial={prefersReducedMotion ? false : { opacity: 0, y: 12 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ delay: 0.15 + index * 0.05, duration: 0.35 }}
								className={question.options.length === 2 ? "sm:col-span-2" : undefined}
							>
								<ChoiceCard
									title={option.title}
									description={option.description}
									icon={option.icon}
									accent={option.accent}
									onClick={option.onClick}
								/>
							</motion.div>
						))}
					</div>
				</main>
			) : (
				<PartialLoadingPage />
			)}
		</Layout>
	)
}

export default SelectIntentPage
